import CloseIcon from '@mui/icons-material/Close';
import DoneIcon from '@mui/icons-material/Done';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Box,
  Button,
  Chip,
  Grow,
  LinearProgress,
  Skeleton,
  Slide,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { TransitionGroup } from 'react-transition-group';
import AxiosConfig from '../../../../config/AxiosConfig';
import {
  sessionInitCall,
  sessionTerminate,
} from '../../../helpers/utils/jsSipUserAgent';
import { checkAccessAdministrator } from '../../../helpers/utils/RoleManager';
import useTask from '../../../hooks/project/useTask';
import useTicket from '../../../hooks/project/useTicket';
import useUsers from '../../../hooks/utils/useUsers';
import BrandIcon from '../../utils/common/BrandIcon';
import LoadingButton from '../../utils/common/LoadingButton';
import TicketStatusChip from '../../utils/common/TicketStatusChip';
import UserAssignmentAutocomplete from '../../utils/common/UserAssignmentAutocomplete';
import PhoneSplitButton from './PhoneSplitButton.jsx';
import ProjectTicketDetails from './ProjectTicketDetails.jsx';

const ProjectTicket = (props) => {
  const {
    isCurrentTicket,
    ticket,
    tickets,
    orders,
    project,
    setOpenNewTicketDialog,
    setNewTicketDialogMessage,
    displayAddNoteDialog,
    handleDisplayAddNoteDialog,
    mutate,
  } = props;
  const profile = useSelector((state) => state?.login?.profile);
  const navigate = useNavigate();
  const [selectedUser, setSelectedUser] = useState(ticket?.assignedTo || null);
  const {
    ticket: ticketDetails,
    mutate: mutateTicket,
    isValidating: isValidating,
    isLoading: isLoading,
  } = useTicket(isCurrentTicket ? ticket?._id : null);
  const { tasks, mutate: mutateTask } = useTask(
    isCurrentTicket ? ticket?._id : null
  );
  const { mutate: mutateUsers, isValidating: isValidatingUsers } = useUsers(
    profile?._id
  );
  const [value, setValue] = useState(ticket?.order);
  const [orderEdit, setOrderEdit] = useState(false);
  const [options, setOptions] = useState(orders);
  const [isLoadingOrder, setIsLoadingOrder] = useState(false);
  const [isLoadingAssign, setIsLoadingAssign] = useState(false);
  const [session, setSession] = useState(null);
  const [phoneList, setPhoneList] = useState(ticketDetails?.phoneList || []);
  const [phoneCallId, setPhoneCallId] = useState(null);
  const [isFollowed, setIsFollowed] = useState(false);
  const [observers, setObservers] = useState([]);

  useEffect(() => {
    if (!ticketDetails?.observers || !profile?._id) {
      setIsFollowed(false);
      setObservers([]);
      return;
    }

    setIsFollowed(
      ticketDetails.observers.some((obs) => obs?._id === profile._id)
    );
    setObservers(ticketDetails.observers.map((obs) => obs.email));
  }, [ticketDetails, profile]);

  useEffect(() => {
    if (
      selectedUser !== undefined &&
      ticket?.assignedTo !== undefined &&
      selectedUser?._id !== ticket?.assignedTo?._id
    ) {
      setIsLoadingAssign(true);
      AxiosConfig.create('post', `/tickets/${ticket?._id}/assignUser`)
        .call({
          assignedBy: profile?._id,
          assignedTo: selectedUser?._id,
        })
        .exit((_res) => {
          mutateTicket();
          mutate();
          mutateUsers();
          setIsLoadingAssign(false);
        })
        .catch((err) => console.error(err));
    }
  }, [selectedUser]);

  useEffect(() => {
    setPhoneList(ticketDetails?.phoneList);
  }, [ticketDetails]);

  useEffect(() => {
    setOptions(orders);
  }, [orders]);

  const handleDisplayTicket = (ticketId) => {
    if (isCurrentTicket) {
      navigate(`/project/${project?._id}/tickets`);
    } else {
      navigate(`/project/${project?._id}/tickets/${ticketId}`);
    }
  };

  const handleOrderChange = (newOrder) => {
    setIsLoadingOrder(true);
    AxiosConfig.create('put', `/tickets/${ticket?._id}/changeOrder/`)
      .call({
        orderId: newOrder?._id,
      })
      .exit((_res) => {
        mutate();
        mutateTicket();
        setIsLoadingOrder(false);
      })
      .catch((err) => console.error(err))
      .finally(() => {
        setValue(newOrder);
      });
  };

  const handleStartPhoneCall = (phoneNumber) => {
    if (!session) {
      setSession(
        sessionInitCall(
          profile,
          phoneNumber,
          ticket?._id,
          setSession,
          mutateTicket,
          (phoneCallId) => setPhoneCallId(phoneCallId)
        )
      );
    }
  };

  const handleEndPhoneCall = (_e) => {
    if (session) {
      sessionTerminate(
        session,
        {
          // status_code: 486, // BUSY status code
          reason_phrase: 'BYE', // BUSY
        },
        mutateTicket
      );
      setSession(null);
    }
  };

  const toggleFollow = async () => {
    try {
      setIsFollowed((prev) => !prev);
      const response = await AxiosConfig.create(
        'post',
        `/tickets/${ticket?._id}/toggleFollow`
      ).call({ userId: profile?._id });

      if (response !== null) {
        setIsFollowed(response);
      }

      mutateTicket();
      mutate();
    } catch (error) {
      console.error('Erreur lors du suivi du ticket', error);
      setIsFollowed((prev) => !prev);
    }
  };

  return (
    <Accordion
      elevation={20}
      disableGutters
      expanded={isCurrentTicket}
      sx={{
        margin: 1,
        // overflow: 'hidden',
        // paddingBottom: 1,
        height: '100%',
      }}
    >
      <AccordionSummary
        component="div"
        expandIcon={
          <ExpandMoreRoundedIcon
            onClick={(_e) => handleDisplayTicket(ticket?._id)}
          />
        }
        sx={[
          {
            userSelect: 'text',
            // marginTop: 1,
            backgroundColor: isCurrentTicket ? 'primary' : 'inherit',
          },
        ]}
      >
        <Box
          sx={{
            alignItems: 'center',
            textAlign: 'auto',
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
          }}
        >
          <Box onClick={(_e) => handleDisplayTicket(ticket?._id)}>
            <BrandIcon
              brand={ticket?.contact?.store?.brand?.name}
              type={ticket?.contact?.store?.contactType?.codeBO}
              style={{
                width: 'inherit',
                height: '32',
              }}
            />
          </Box>
          <Box>
            <Tooltip title={isFollowed ? 'Ne plus suivre' : 'Suivre'}>
              <Box
                component="span"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleFollow();
                }}
                sx={{
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '6px',
                  borderRadius: '4px',
                  color: isFollowed ? 'warning.main' : 'text.primary',
                  '&:hover': {
                    backgroundColor: 'action.hover',
                  },
                }}
              >
                {isFollowed ? <StarIcon /> : <StarBorderIcon />}
              </Box>
            </Tooltip>
            {checkAccessAdministrator(profile) && (
              <Tooltip
                title={
                  observers.length > 0
                    ? observers.join(', ')
                    : 'Personne ne suit ce ticket'
                }
              >
                <Box
                  component="span"
                  sx={{
                    cursor: 'pointer',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '6px',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  {observers.length > 0 ? (
                    <VisibilityIcon color="primary" />
                  ) : (
                    <VisibilityOffIcon color="disabled" />
                  )}
                </Box>
              </Tooltip>
            )}
          </Box>

          {/* <Box>
            <PriorityFlag priority={ticket?.priority?.name} />
          </Box> */}
          <Box>
            {ticket?.isOpen ? (
              <Chip label="Ouvert" color="error" size="small" />
            ) : (
              <Chip label="Fermé" color="success" size="small" />
            )}
          </Box>
          <Box>
            <TicketStatusChip
              status={ticket?.status}
              hasActiveTask={ticket?.hasActiveTask}
            />
          </Box>
          <Box>
            <Tooltip
              placement="top"
              align="center"
              title={ticket?.contact?.store?.contactType?.name}
            >
              <Typography align="center" component={'span'}>
                {ticket?.reference}
              </Typography>
            </Tooltip>
          </Box>
          {/* <Box>
            <TicketTypeIcon
              ticketType={ticket?.ticketType?.name}
              service={ticket?.service?.code}
            />
          </Box> */}
          <Box
            sx={{
              display: 'flex',
              // flexDirection: "column",
              justifyContent: 'center',
              alignItems: 'center',
            }}
          >
            <Switch
              size="small"
              checked={orderEdit}
              onChange={() => setOrderEdit(!orderEdit)}
              disabled={ticketDetails?.hasExistingOrderAction || !options}
            />
            {!orderEdit ? (
              <Typography align="center" component={'span'} noWrap={true}>
                {ticket?.order?.reference || 'N./A.'}
                {ticket?.order?.mpId && ` (${ticket.order.mpId})`}
              </Typography>
            ) : (
              <>
                <Autocomplete
                  options={options}
                  value={value}
                  size="small"
                  onChange={(event, newOrder) => {
                    setValue(newOrder || 'N./A.');
                  }}
                  getOptionLabel={(option) => {
                    return `${option?.reference ? option?.reference : 'N./A.'}`;
                  }}
                  isOptionEqualToValue={(option, value) =>
                    option?._id === value?._id || value === 'N./A.'
                  }
                  sx={{ width: 270 }}
                  renderInput={(params) => (
                    <TextField {...params} label="Commande" />
                  )}
                />
                <Box sx={{ paddingLeft: 1, display: 'flex', gap: 1 }}>
                  <LoadingButton
                    variant="contained"
                    isLoading={isLoadingOrder}
                    loadingText={false}
                    color="success"
                    onClick={() => {
                      handleOrderChange(value);
                    }}
                    size="small"
                    disabled={
                      value?.reference === ticket?.order?.reference ||
                      (!ticket?.order?.reference && value === 'N./A.')
                    }
                  >
                    <DoneIcon />
                  </LoadingButton>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => {
                      setOrderEdit(false);
                    }}
                    size="small"
                  >
                    <CloseIcon />
                  </Button>
                </Box>
              </>
            )}
          </Box>
          <Box>
            <>
              {isLoadingAssign || isValidatingUsers ? (
                <LinearProgress color="info" sx={{ margin: 1 }} />
              ) : (
                <UserAssignmentAutocomplete
                  type={'ticket'}
                  assignmentId={ticket?.assignedTo?._id}
                  setSelectedUser={setSelectedUser}
                  selectedUser={selectedUser}
                />
              )}
            </>
          </Box>
          {import.meta.env.VITE_HOORTEL_ENABLED === 'true' ? (
            <Box>
              <PhoneSplitButton
                isTicketExpanded={ticket?._id}
                countryCode2={ticket?.contact?.store?.country?.code2?.toLowerCase()}
                phoneList={phoneList}
                handleStartPhoneCall={handleStartPhoneCall}
                phoneCallId={phoneCallId}
                session={session}
                handleEndPhoneCall={handleEndPhoneCall}
              />
            </Box>
          ) : null}
          <Box>
            <Typography align="center" component={'span'}>
              {new Date(ticket?.lastMessageAt).toLocaleString('fr-FR', {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: 'numeric',
                minute: 'numeric',
              })}
            </Typography>
          </Box>
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {isLoading ? (
          <Box
            sx={{
              display: 'flex',
              height: 'auto',
              width: 'auto',
              justifyContent: 'space-around',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                width: '50%',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                gap: 1,
                marginTop: 5,
              }}
            >
              <Skeleton variant="text" width={900} />
              <Skeleton variant="rounded" width={900} height={250} />
              <Skeleton variant="text" width={900} />
              <Skeleton variant="rounded" width={900} height={250} />
            </Box>
            <Box
              sx={{
                display: 'flex',
                width: '50%',
                justifyContent: 'center',
                alignItems: 'center',
                flexDirection: 'column',
                gap: 1,
                marginTop: 5,
              }}
            >
              <Skeleton variant="text" width={700} />
              <Skeleton variant="rounded" width={700} height={250} />
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-around',
                  width: '100%',
                }}
              >
                <Skeleton variant="rounded" width={100} height={30} />
                <Skeleton variant="rounded" width={100} height={30} />
                <Skeleton variant="rounded" width={100} height={30} />
              </Box>
            </Box>
          </Box>
        ) : (
          <ProjectTicketDetails
            validating={isValidating}
            tickets={tickets}
            orders={orders}
            ticket={ticket?._id ? ticketDetails : ticket}
            tasks={tasks}
            mutateTask={mutateTask}
            setOpenNewTicketDialog={setOpenNewTicketDialog}
            setNewTicketDialogMessage={setNewTicketDialogMessage}
            displayAddNoteDialog={displayAddNoteDialog}
            handleDisplayAddNoteDialog={handleDisplayAddNoteDialog}
            mutate={mutate}
            mutateTicket={mutateTicket}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const ProjectTickets = (props) => {
  const {
    tickets,
    orders,
    project,
    currentTicket,
    setOpenNewTicketDialog,
    setNewTicketDialogMessage,
    displayAddNoteDialog,
    handleDisplayAddNoteDialog,
    mutate,
    location,
  } = props;

  return (
    <Box
      sx={{ width: 'auto', height: 'auto', margin: 0 }}
      style={{
        overflow: 'auto',
      }}
    >
      {currentTicket ? (
        <Grow in={Boolean(currentTicket?._id)} mountOnEnter unmountOnExit>
          <Box>
            <ProjectTicket
              isCurrentTicket={true}
              ticket={currentTicket}
              tickets={tickets}
              orders={orders}
              project={project}
              setOpenNewTicketDialog={setOpenNewTicketDialog}
              setNewTicketDialogMessage={setNewTicketDialogMessage}
              displayAddNoteDialog={displayAddNoteDialog}
              handleDisplayAddNoteDialog={handleDisplayAddNoteDialog}
              mutate={mutate}
              location={location}
            />
          </Box>
        </Grow>
      ) : (
        <TransitionGroup>
          {tickets?.map((ticket) => {
            return (
              <Slide
                key={ticket?._id}
                in={Boolean(ticket?._id)}
                mountOnEnter
                unmountOnExit
              >
                <div>
                  <ProjectTicket
                    isCurrentTicket={false}
                    ticket={ticket}
                    tickets={tickets}
                    project={project}
                    setOpenNewTicketDialog={setOpenNewTicketDialog}
                    setNewTicketDialogMessage={setNewTicketDialogMessage}
                    displayAddNoteDialog={displayAddNoteDialog}
                    handleDisplayAddNoteDialog={handleDisplayAddNoteDialog}
                    mutate={mutate}
                  />
                </div>
              </Slide>
            );
          })}
        </TransitionGroup>
      )}
    </Box>
  );
};

export default ProjectTickets;
